/**
 * SHOPIFY CHAT WIDGET - Direct N8N Integration
 * Performance Optimized with Lazy Loading
 */

class DirectN8NChatWidget {
  constructor() {
    this.isOpen = false;
    this.customerType = null;
    this.customerEmail = null;
    this.webhookUrl = 'https://somna-live-chat.syncmywork.workers.dev';
    this.sessionId = this.getOrCreateSessionId();
    this.botAvatarUrl = null;
    this.chatHistory = [];
    this.customerIdentified = false;
    this.hasUserMessages = false;
    this.notificationTimer = null;
    this.hasInteracted = false;
    this.pendingMessage = null;
    this.retryTimer = null;
    this.inactivityTimer = null;
    this.lastUserActivity = null;
    this.followUpSent = false;
    this.pendingFollowUps = 0;
    this.followUpCount = 0;
    this.isOnline = navigator.onLine;
    this.maxRetries = 3;
    this.soundEnabled = this.loadSoundPreference();
    this.initialized = false;
    this._scrollCache = {};
    this.updateUIDebounced = this.debounce(this.updateUI.bind(this), 16);

    this.questionSets = {
      existing: [
        { icon: '📦', text: 'Track my order', question: 'How can I track my order?' },
        { icon: '🔄', text: 'Return/exchange', question: 'What is your return and exchange policy?' },
        { icon: '🚚', text: 'Delivery status', question: 'When will my bed/mattress be delivered?' },
        { icon: '💷', text: 'Payment issue', question: 'I have a question about my payment or invoice' },
        { icon: '🛏️', text: 'Product support', question: 'I need help with my bed or mattress' },
        { icon: '📞', text: 'Speak to someone', question: 'Can I speak to a customer service representative?' }
      ],
      new: [
        { icon: '🛏️', text: 'Bed types', question: 'What types of beds do you offer?' },
        { icon: '🌙', text: 'Mattress guide', question: 'Which mattress is best for me?' },
        { icon: '💷', text: 'Pricing info', question: 'What are your current prices and offers?' },
        { icon: '🚚', text: 'UK delivery', question: 'Do you deliver throughout the UK?' },
        { icon: '🏪', text: 'Store locations', question: 'Where are your showrooms located?' },
        { icon: '📏', text: 'Sizes available', question: 'What bed and mattress sizes do you have?' }
      ]
    };

    this.setupConnectionMonitoring();
    
    // Ensure DOM is ready before initialization
    this.ensureReady();
  }

  ensureReady() {
    // Multiple fallback strategies for DOM readiness
    if (document.readyState === 'loading') {
      // DOM not ready yet
      document.addEventListener('DOMContentLoaded', () => this.initLazy());
    } else if (document.readyState === 'interactive' || document.readyState === 'complete') {
      // DOM is ready
      this.initLazy();
    } else {
      // Fallback - wait a bit and try again
      setTimeout(() => this.ensureReady(), 50);
    }
  }

  initLazy() {
    console.log('🔧 Initializing Chat Widget (Lazy)...');
    
    // Wait a bit to ensure DOM is fully rendered
    setTimeout(() => {
      const toggle = document.getElementById('chat-toggle');
      if (toggle) {
        console.log('✅ Chat toggle found, binding click event');
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          console.log('🔧 Chat toggle clicked!');
          this.initialize();
        }, { once: true });
        
        // Add immediate visual feedback
        toggle.style.cursor = 'pointer';
        toggle.style.pointerEvents = 'auto';
        
        this.startNotificationTimer();
        console.log('✅ Chat Widget lazy init complete!');
      } else {
        console.error('❌ Chat toggle not found, retrying...');
        // Retry after a short delay
        setTimeout(() => this.initLazy(), 100);
      }
    }, 100);
  }

  init() {
    if (this.initialized) return;
    
    console.log('🔧 Full initialization...');
    const container = document.getElementById('chat-widget-container');
    const toggle = document.getElementById('chat-toggle');
    const chatWindow = document.getElementById('chat-window');

    if (!container || !toggle || !chatWindow) {
      console.error('❌ Chat widget elements not found');
      return;
    }

    console.log('✅ All elements found, binding events...');
    this.bindEvents();
    this.setWelcomeTime();
    this.loadFavicon();
    this.loadChatHistory();
    this.initializeSoundButton();
    
    this.initialized = true;
    console.log('✅ Direct N8N Chat Widget fully initialized!');
  }

  initialize() {
    if (this.initialized) return this.toggleChat();
    this.init();
    this.toggleChat();
  }

  bindEvents() {
    const toggleBtn = document.getElementById('chat-toggle');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('🔧 Chat toggle clicked');
        this.toggleChat();
      });
    }

    const soundToggleBtn = document.getElementById('sound-toggle');
    if (soundToggleBtn) {
      soundToggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSound();
      });
    }

    const closeBtn = document.getElementById('chat-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('🔧 Chat close clicked');
        this.closeChat();
      });
    }

    const verifyBtn = document.getElementById('verify-email');
    if (verifyBtn) {
      verifyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleExistingCustomer();
      });
    }

    const newCustomerBtn = document.getElementById('new-customer-btn');
    if (newCustomerBtn) {
      newCustomerBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleNewCustomer();
      });
    }

    const emailInput = document.getElementById('customer-email');
    if (emailInput) {
      emailInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleExistingCustomer();
        }
      });
    }

    const chatInput = document.getElementById('chat-input');
    if (chatInput) {
      chatInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.sendMessage();
        }
      });
      chatInput.addEventListener('input', () => {
        this.resetUserActivity();
      });
      chatInput.addEventListener('focus', () => {
        this.resetUserActivity();
      });
    }

    const sendBtn = document.getElementById('send-btn');
    if (sendBtn) {
      sendBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.sendMessage();
      });
    }

    this.bindSuggestionButtons();
  }

  setupConnectionMonitoring() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.updateConnectionStatus();
      console.log('🌐 Connection restored');
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.updateConnectionStatus();
      console.log('📴 Connection lost');
    });
  }

  updateConnectionStatus() {
    const statusText = document.querySelector('.chat-status');
    if (statusText) {
      if (this.isOnline) {
        statusText.innerHTML = '<span class="status-dot"></span>Online now';
      } else {
        statusText.innerHTML = '<span class="status-dot offline"></span>Currently offline';
      }
    }
  }

  bindSuggestionButtons() {
    const suggestionBtns = document.querySelectorAll('.suggestion-btn');
    suggestionBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const question = btn.getAttribute('data-question');
        if (question && this.customerIdentified) {
          document.getElementById('chat-input').value = question;
          this.sendMessage();
          this.hideSuggestedQuestions();
        }
      });
    });
  }

  hideSuggestedQuestions() {
    const suggestions = document.getElementById('suggested-questions');
    if (suggestions) {
      suggestions.style.display = 'none';
    }
  }

  toggleChat() {
    console.log('🔧 toggleChat called, current isOpen:', this.isOpen);
    const chatWindow = document.getElementById('chat-window');
    const toggleBtn = document.getElementById('chat-toggle');
    
    if (!chatWindow) {
      console.error('❌ Chat window not found');
      return;
    }

    this.isOpen = !this.isOpen;
    
    if (this.isOpen) {
      chatWindow.style.display = 'flex';
      chatWindow.classList.add('open');
      chatWindow.setAttribute('aria-hidden', 'false');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
      
      this.hideNotification();
      this.hideFollowUpNotification();
      this.processPendingFollowUps();
      this.resetUserActivity();
      
      console.log('✅ Chat opened');
      setTimeout(() => {
        const input = document.getElementById('chat-input');
        if (input && this.customerIdentified) {
          input.focus();
        }
      }, 300);
    } else {
      chatWindow.style.display = 'none';
      chatWindow.classList.remove('open');
      chatWindow.setAttribute('aria-hidden', 'true');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
      
      this.resetUserActivity();
      console.log('✅ Chat closed');
    }
  }

  closeChat() {
    this.isOpen = true;
    this.toggleChat();
  }

  toggleSound() {
    this.soundEnabled = !this.soundEnabled;
    const soundOnIcon = document.getElementById('sound-on-icon');
    const soundOffIcon = document.getElementById('sound-off-icon');

    if (this.soundEnabled) {
      soundOnIcon.style.display = 'block';
      soundOffIcon.style.display = 'none';
      console.log('🔊 Sound notifications enabled');
      // Play a test sound to confirm it's working
      this.playNotificationSound();
    } else {
      soundOnIcon.style.display = 'none';
      soundOffIcon.style.display = 'block';
      console.log('🔇 Sound notifications disabled');
    }

    // Save sound preference
    try {
      localStorage.setItem('shopify_chat_sound_enabled', this.soundEnabled.toString());
    } catch (e) {
      console.log('Could not save sound preference');
    }
  }

  handleExistingCustomer() {
    const emailInput = document.getElementById('customer-email');
    if (!emailInput) {
      console.error('❌ Email input not found');
      return;
    }

    const email = emailInput.value.trim();
    if (!email) {
      alert('Please enter your email address');
      return;
    }

    if (!this.isValidEmail(email)) {
      alert('Please enter a valid email address');
      return;
    }

    this.customerType = 'existing';
    this.customerEmail = email;
    this.customerIdentified = true;
    
    console.log('✅ Customer identified as existing:', email);
    this.showChatInterface();
  }

  handleNewCustomer() {
    this.customerType = 'new';
    this.customerEmail = null;
    this.customerIdentified = true;
    
    console.log('✅ Customer identified as new');
    this.showChatInterface();
  }

  showChatInterface() {
    const identification = document.getElementById('customer-identification');
    const messages = document.getElementById('chat-messages');
    const inputContainer = document.getElementById('chat-input-container');

    if (identification) identification.style.display = 'none';
    if (messages) messages.style.display = 'flex';
    if (inputContainer) inputContainer.style.display = 'block';

    this.populateSuggestedQuestions();
    
    setTimeout(() => {
      const input = document.getElementById('chat-input');
      if (input) input.focus();
    }, 300);

    this.saveChatHistory();
    this.startInactivityTimer();
  }

  populateSuggestedQuestions() {
    const buttonsContainer = document.getElementById('suggestion-buttons');
    if (!buttonsContainer || !this.customerType) return;

    const questions = this.questionSets[this.customerType] || this.questionSets.new;
    buttonsContainer.innerHTML = '';

    questions.forEach(q => {
      const button = document.createElement('button');
      button.className = 'suggestion-btn';
      button.setAttribute('data-question', q.question);
      button.innerHTML = `${q.icon} ${q.text}`;
      buttonsContainer.appendChild(button);
    });

    this.bindSuggestionButtons();
  }

  async sendMessage() {
    const input = document.getElementById('chat-input');
    if (!input) return;

    const message = this.sanitizeInput(input.value.trim());
    if (!message) return;

    if (!this.isOnline) {
      this.showErrorMessage('You appear to be offline. Please check your internet connection.');
      return;
    }

    if (!this.isValidMessage(message)) {
      this.showErrorMessage('Please enter a valid message (1-1000 characters).');
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.pendingMessage) {
      console.log('⚠️ Message already in progress, ignoring...');
      return;
    }

    console.log('📤 Sending message:', message);
    this.addMessage(message, 'user');
    input.value = '';
    this.showTyping();

    const pendingId = Date.now() + '_' + Math.random().toString(36).substr(2, 5);
    this.pendingMessage = {
      id: pendingId,
      message: message,
      timestamp: new Date().toISOString(),
      retryCount: 0
    };

    this.saveChatHistory();

    try {
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          message: message,
          customerType: this.customerType,
          customerEmail: this.customerEmail,
          sessionId: this.sessionId,
          shopifyData: this.getShopifyData(),
          pendingId: pendingId
        })
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📨 Received response:', data);

      this.hideTyping();
      this.pendingMessage = null;
      this.saveChatHistory();

      let botMessage = 'Thank you for your message!';
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (firstItem.type === 'markdown' && firstItem.output) {
          botMessage = firstItem.output;
        }
      } else {
        botMessage = data.output || data.message || data.response || 'Thank you for your message!';
      }

      this.addMessage(this.sanitizeInput(botMessage), 'bot');
      this.playNotificationSound();

    } catch (error) {
      console.error('❌ Chat error:', error);
      this.hideTyping();
      
      if (error.name === 'TypeError' && !navigator.onLine) {
        console.log('📴 Network error detected, user appears offline');
        this.handlePendingMessageError(new Error('You appear to be offline. Please check your internet connection.'));
      } else {
        console.log('⚠️ Unexpected error type:', error.name, error.message);
        this.handlePendingMessageError(error);
      }
    }
  }

  addMessage(content, sender) {
    const timestamp = new Date().toISOString();
    this.chatHistory.push({ content: content, sender: sender, timestamp: timestamp });

    if (sender === 'user') {
      this.hasUserMessages = true;
      this.followUpCount = 0;
      this.startInactivityTimer();
      this.hideSuggestedQuestions();
    }

    this.saveChatHistory();
    this.renderMessage(content, sender, timestamp);
  }

  restoreMessage(content, sender, timestamp) {
    this.renderMessage(content, sender, timestamp, false);
  }

  renderMessage(content, sender, timestamp, shouldScroll = true) {
    const messagesContainer = document.getElementById('chat-messages');
    if (!messagesContainer) return;

    const messageDiv = document.createElement('div');
    messageDiv.className = `message ${sender}-message`;

    let avatarHtml = '';
    if (sender === 'bot') {
      if (this.botAvatarUrl) {
        avatarHtml = `<div class="message-avatar"><img src="${this.botAvatarUrl}" alt="Support"></div>`;
      } else {
        avatarHtml = '<div class="message-avatar"><svg class="default-avatar" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg></div>';
      }
    }

    const processedContent = this.parseMarkdown(content);
    const timeStr = this.formatTime(new Date(timestamp));

    messageDiv.innerHTML = `${avatarHtml}<div class="message-content"><div class="message-text">${processedContent}</div><div class="message-time">${timeStr}</div></div>`;
    
    messagesContainer.appendChild(messageDiv);
    
    if (shouldScroll) {
      this.smartScroll(messagesContainer, messageDiv, sender);
    }

    console.log(`💬 Rendered ${sender} message:`, content);
  }

  smartScroll(messagesContainer, messageDiv, sender) {
    if (sender === 'user') {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      return;
    }
    
    // For bot messages, always scroll to show the start of the message
    const botMessageTop = messageDiv.offsetTop;
    const targetScrollTop = Math.max(0, botMessageTop - 20);
    messagesContainer.scrollTop = targetScrollTop;
    console.log('📱 Scrolled to start of bot message');
  }

  showTyping() {
    const typing = document.getElementById('chat-typing');
    if (!typing) return;

    if (this.botAvatarUrl) {
      const typingAvatar = document.getElementById('typing-bot-avatar');
      if (typingAvatar) {
        typingAvatar.src = this.botAvatarUrl;
        typingAvatar.style.display = 'block';
        
        // Add custom-logo class if using Shopify media logo
        if (this.isCustomLogo) {
          typingAvatar.classList.add('custom-logo');
        } else {
          typingAvatar.classList.remove('custom-logo');
        }
      }
      const defaultAvatar = typing.querySelector('.typing-default-avatar');
      if (defaultAvatar) defaultAvatar.style.display = 'none';
    }

    typing.style.display = 'flex';
    
    const messagesContainer = document.getElementById('chat-messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  hideTyping() {
    const typing = document.getElementById('chat-typing');
    if (typing) typing.style.display = 'none';
  }

  parseMarkdown(text) {
    if (!text) return '';
    
    let html = text
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');

    if (html.includes('<li>')) {
      html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
    }

    if (!html.startsWith('<p>') && html.includes('</p>')) {
      html = '<p>' + html;
    }

    if (html.includes('<p>') && !html.endsWith('</p>')) {
      html += '</p>';
    }

    return html;
  }

  formatTime(date) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  setWelcomeTime() {
    const welcomeTime = document.getElementById('welcome-time');
    if (welcomeTime) welcomeTime.textContent = this.formatTime(new Date());
  }

  loadFavicon() {
    // Primary logo - your Shopify media logo
    const primaryLogoUrl = 'https://cdn.shopify.com/s/files/1/0745/6129/0491/files/Artboard_1_4x-8.png?v=1760971385';
    
    // Fallback - site favicon
    const favicon = document.querySelector('link[rel="icon"], link[rel="shortcut icon"]');
    const fallbackLogoUrl = favicon ? favicon.href : null;
    
    // Try to load primary logo first
    this.loadAvatarWithFallback(primaryLogoUrl, fallbackLogoUrl);
  }
  
  loadAvatarWithFallback(primaryUrl, fallbackUrl) {
    const img = new Image();
    
    img.onload = () => {
      // Primary logo loaded successfully
      this.botAvatarUrl = primaryUrl;
      this.isCustomLogo = true; // Flag to identify custom Shopify logo
      this.setAvatarImages(primaryUrl, true);
      console.log('✅ Loaded primary logo:', primaryUrl);
    };
    
    img.onerror = () => {
      // Primary logo failed, try fallback
      if (fallbackUrl) {
        const fallbackImg = new Image();
        
        fallbackImg.onload = () => {
          // Fallback loaded successfully
          this.botAvatarUrl = fallbackUrl;
          this.isCustomLogo = false; // Flag to identify favicon fallback
          this.setAvatarImages(fallbackUrl, false);
          console.log('⚠️ Primary logo failed, using favicon fallback:', fallbackUrl);
        };
        
        fallbackImg.onerror = () => {
          // Both failed, use default SVG avatars
          this.botAvatarUrl = null;
          this.isCustomLogo = false;
          console.log('❌ Both logos failed, using default avatars');
        };
        
        fallbackImg.src = fallbackUrl;
      } else {
        // No fallback available
        this.botAvatarUrl = null;
        this.isCustomLogo = false;
        console.log('❌ Primary logo failed, no fallback available');
      }
    };
    
    // Start loading primary logo
    img.src = primaryUrl;
  }
  
  setAvatarImages(logoUrl, isCustomLogo = false) {
    // Update all avatar instances
    const botAvatar = document.getElementById('bot-avatar');
    if (botAvatar) {
      botAvatar.src = logoUrl;
      botAvatar.style.display = 'block';
      // Add custom-logo class only for Shopify media logo (not favicon)
      if (isCustomLogo) {
        botAvatar.classList.add('custom-logo');
      } else {
        botAvatar.classList.remove('custom-logo');
      }
    }
    
    const headerAvatar = document.getElementById('header-avatar');
    if (headerAvatar) {
      headerAvatar.src = logoUrl;
      headerAvatar.style.display = 'block';
      // Add custom-logo class only for Shopify media logo (not favicon)
      if (isCustomLogo) {
        headerAvatar.classList.add('custom-logo');
      } else {
        headerAvatar.classList.remove('custom-logo');
      }
    }
    
    const typingBotAvatar = document.getElementById('typing-bot-avatar');
    if (typingBotAvatar) {
      typingBotAvatar.src = logoUrl;
      typingBotAvatar.style.display = 'block';
      // Add custom-logo class only for Shopify media logo (not favicon)
      if (isCustomLogo) {
        typingBotAvatar.classList.add('custom-logo');
      } else {
        typingBotAvatar.classList.remove('custom-logo');
      }
    }
    
    // Hide default SVG avatars when custom logo is used
    const defaultAvatar = document.querySelector('.default-avatar');
    if (defaultAvatar) defaultAvatar.style.display = 'none';
    
    const headerDefaultAvatar = document.querySelector('.header-default-avatar');
    if (headerDefaultAvatar) headerDefaultAvatar.style.display = 'none';
    
    const typingDefaultAvatar = document.querySelector('.typing-default-avatar');
    if (typingDefaultAvatar) typingDefaultAvatar.style.display = 'none';
  }

  generateSessionId() {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  getOrCreateSessionId() {
    try {
      const saved = sessionStorage.getItem('shopify_chat_history');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.sessionId) {
          return data.sessionId;
        }
      }
    } catch (error) {
      console.error('Failed to load existing session ID:', error);
    }
    return this.generateSessionId();
  }

  loadSoundPreference() {
    try {
      const saved = localStorage.getItem('shopify_chat_sound_enabled');
      if (saved !== null) {
        return saved === 'true';
      }
    } catch (error) {
      console.error('Failed to load sound preference:', error);
    }
    return true; // Default to enabled
  }

  initializeSoundButton() {
    const soundOnIcon = document.getElementById('sound-on-icon');
    const soundOffIcon = document.getElementById('sound-off-icon');
    
    if (soundOnIcon && soundOffIcon) {
      if (this.soundEnabled) {
        soundOnIcon.style.display = 'block';
        soundOffIcon.style.display = 'none';
      } else {
        soundOnIcon.style.display = 'none';
        soundOffIcon.style.display = 'block';
      }
      console.log('🔧 Sound button initialized:', this.soundEnabled ? 'enabled' : 'disabled');
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  updateUI() {
    // Batched DOM updates
  }

  destroy() {
    clearTimeout(this.notificationTimer);
    clearTimeout(this.retryTimer);
    clearTimeout(this.inactivityTimer);
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
    this.chatHistory = [];
    this._audio = null;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  sanitizeInput(input) {
    if (typeof input !== 'string') return '';
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  }

  isValidMessage(message) {
    return message && message.length >= 1 && message.length <= 1000;
  }

  showErrorMessage(message) {
    this.addMessage(message, 'bot');
    this.playNotificationSound();
    console.log('⚠️ Error:', message);
  }

  playNotificationSound() {
    if (!this.soundEnabled) return;

    try {
      // Create a pleasant notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Pleasant notification tone (C-E-G chord progression)
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5

      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.4);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);

      console.log('🔊 Playing notification sound');
    } catch (e) {
      console.log('🔇 Web Audio API failed, using fallback:', e);
      // Fallback to simple beep
      try {
        if (!this._audio) {
          this._audio = new Audio('data:audio/wav;base64,UklGRn4AAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YVoAAAC4u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7');
          this._audio.volume = 0.4;
        }
        this._audio.currentTime = 0;
        this._audio.play().catch(() => {
          console.log('🔇 Fallback audio also failed');
        });
      } catch (fallbackError) {
        console.log('🔇 All audio methods failed');
      }
    }
  }

  saveChatHistory() {
    try {
      const historyData = {
        customerType: this.customerType,
        customerEmail: this.customerEmail,
        sessionId: this.sessionId,
        chatHistory: this.chatHistory,
        customerIdentified: this.customerIdentified,
        hasUserMessages: this.hasUserMessages,
        pendingMessage: this.pendingMessage,
        pendingFollowUps: this.pendingFollowUps,
        followUpCount: this.followUpCount,
        timestamp: new Date().toISOString()
      };
      sessionStorage.setItem('shopify_chat_history', JSON.stringify(historyData));
    } catch (error) {
      console.error('Failed to save chat history:', error);
    }
  }

  loadChatHistory() {
    try {
      const saved = sessionStorage.getItem('shopify_chat_history');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.sessionId && data.sessionId === this.sessionId) {
          this.customerType = data.customerType;
          this.customerEmail = data.customerEmail;
          this.chatHistory = data.chatHistory || [];
          this.customerIdentified = data.customerIdentified || false;
          this.hasUserMessages = data.hasUserMessages || false;
          this.pendingMessage = data.pendingMessage || null;
          this.pendingFollowUps = data.pendingFollowUps || 0;
          this.followUpCount = data.followUpCount || 0;

          if (this.customerIdentified) {
            this.showChatInterface();
          }

          if (this.chatHistory.length > 0) {
            const messagesContainer = document.getElementById('chat-messages');
            if (messagesContainer) {
              const welcomeMessage = messagesContainer.querySelector('.welcome-message');
              if (welcomeMessage) welcomeMessage.remove();

              this.chatHistory.forEach(msg => {
                this.restoreMessage(msg.content, msg.sender, msg.timestamp);
              });

              setTimeout(() => {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
              }, 100);
            }
          }

          if (this.pendingMessage) {
            this.handlePendingMessage();
          }

          console.log('✅ Loaded chat history');
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  }

  getShopifyData() {
    let deviceType = 'desktop';
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      deviceType = 'mobile';
    } else if (/Tablet|iPad/i.test(navigator.userAgent)) {
      deviceType = 'tablet';
    }

    return {
      dynamicInfo: {
        currentUserPageFullUrl: window.location.href,
        productTitle: typeof Shopify !== 'undefined' && Shopify.product ? Shopify.product.title : null,
        productId: typeof Shopify !== 'undefined' && Shopify.product ? Shopify.product.id : null,
        pageTitle: document.title,
        productType: typeof Shopify !== 'undefined' && Shopify.product ? Shopify.product.type : null,
        deviceType: deviceType
      }
    };
  }

  getUrlParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  startNotificationTimer() {
    console.log('🔔 Starting notification timer (40 seconds)...');
    this.notificationTimer = setTimeout(() => {
      if (!this.hasInteracted && !this.isOpen) {
        this.showNotification();
      }
    }, 40000);
  }

  showNotification() {
    console.log('🔔 Showing notification badge');
    const notificationBadge = document.getElementById('chat-notification-badge');
    if (notificationBadge) notificationBadge.style.display = 'flex';
  }

  hideNotification() {
    console.log('🔔 Hiding notification badge');
    this.hasInteracted = true;
    
    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer);
      this.notificationTimer = null;
    }
    
    const notificationBadge = document.getElementById('chat-notification-badge');
    if (notificationBadge) notificationBadge.style.display = 'none';
  }

  handlePendingMessage() {
    if (!this.pendingMessage) return;
    
    console.log('🔄 Found pending message, attempting retry...');
    this.showTyping();
    this.retryPendingMessage();
  }

  handlePendingMessageError(error) {
    if (this.pendingMessage) {
      this.pendingMessage.retryCount++;
      if (this.pendingMessage.retryCount < 3) {
        console.log(`🔄 Retrying pending message (attempt ${this.pendingMessage.retryCount + 1}/3)...`);
        this.retryTimer = setTimeout(() => {
          this.retryPendingMessage();
        }, 2000);
        return;
      }
    }

    this.pendingMessage = null;
    this.saveChatHistory();
    this.addMessage(`Sorry, I encountered an error: ${error.message}. Please try again.`, 'bot');
    this.playNotificationSound();
  }

  async retryPendingMessage() {
    if (!this.pendingMessage) return;

    try {
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          message: this.pendingMessage.message,
          customerType: this.customerType,
          customerEmail: this.customerEmail,
          sessionId: this.sessionId,
          shopifyData: this.getShopifyData(),
          pendingId: this.pendingMessage.id
        })
      });

      console.log('📡 Retry response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📨 Retry received response:', data);

      this.hideTyping();
      this.pendingMessage = null;
      this.saveChatHistory();

      let botMessage = 'Thank you for your message!';
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (firstItem.type === 'markdown' && firstItem.output) {
          botMessage = firstItem.output;
        }
      } else {
        botMessage = data.output || data.message || data.response || 'Thank you for your message!';
      }

      this.addMessage(botMessage, 'bot');
      this.playNotificationSound();

    } catch (error) {
      console.error('❌ Retry error:', error);
      console.log('⚠️ Retry error type:', error.name, error.message);
      this.handlePendingMessageError(error);
    }
  }

  startInactivityTimer() {
    if (!this.customerIdentified || !this.hasUserMessages || this.followUpCount >= 3) return;
    
    this.clearInactivityTimer();
    this.lastUserActivity = Date.now();
    this.followUpSent = false;
    
    this.inactivityTimer = setTimeout(() => {
      this.sendFollowUpMessage();
    }, 60000);
  }

  clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  sendFollowUpMessage() {
    if (!this.followUpSent && this.customerIdentified && this.hasUserMessages && this.followUpCount < 3) {
      this.followUpSent = true;
      this.followUpCount++;
      
      const followUpMessages = [
        'Is there anything else I can assist you with?',
        'Do you have any other questions I can help with?',
        'Is there anything more I can help you with today?'
      ];
      
      const randomMessage = followUpMessages[Math.floor(Math.random() * followUpMessages.length)];
      
      if (this.isOpen) {
        this.addLocalMessage(randomMessage, 'bot');
        console.log('💬 Sent follow-up message due to inactivity (chat open) - Count:', this.followUpCount);
      } else {
        this.addPendingFollowUp(randomMessage);
        this.showFollowUpNotification();
        this.playNotificationSound();
        console.log('💬 Added pending follow-up message due to inactivity (chat closed) - Count:', this.followUpCount);
      }
      
      this.startInactivityTimer();
    } else if (this.followUpCount >= 3) {
      console.log('🚫 Follow-up limit reached (3 max), stopping follow-up messages');
    }
  }

  addLocalMessage(content, sender) {
    const timestamp = new Date().toISOString();
    this.chatHistory.push({ content: content, sender: sender, timestamp: timestamp });
    this.saveChatHistory();
    this.renderMessage(content, sender, timestamp);
    
    if (sender === 'bot') {
      this.playNotificationSound();
    }
    
    this.startInactivityTimer();
  }

  resetUserActivity() {
    if (this.customerIdentified && this.hasUserMessages) {
      this.startInactivityTimer();
    }
  }

  addPendingFollowUp(message) {
    const timestamp = new Date().toISOString();
    this.chatHistory.push({ content: message, sender: 'bot', timestamp: timestamp });
    this.pendingFollowUps++;
    this.saveChatHistory();
    console.log('📝 Added pending follow-up message:', message);
  }

  showFollowUpNotification() {
    const badge = document.getElementById('chat-badge');
    if (badge) {
      badge.textContent = this.pendingFollowUps.toString();
      badge.style.display = 'flex';
    }
    console.log('🔔 Showing follow-up notification badge:', this.pendingFollowUps);
  }

  hideFollowUpNotification() {
    const badge = document.getElementById('chat-badge');
    if (badge) {
      badge.style.display = 'none';
    }
    this.pendingFollowUps = 0;
    console.log('🔔 Hiding follow-up notification badge');
  }

  processPendingFollowUps() {
    if (this.pendingFollowUps > 0 && this.chatHistory.length > 0) {
      const messagesContainer = document.getElementById('chat-messages');
      if (messagesContainer) {
        const welcomeMessage = messagesContainer.querySelector('.welcome-message');
        if (welcomeMessage) welcomeMessage.remove();
        
        this.chatHistory.forEach(msg => {
          if (msg.sender === 'bot' && !messagesContainer.querySelector(`[data-timestamp="${msg.timestamp}"]`)) {
            this.restoreMessage(msg.content, msg.sender, msg.timestamp);
            const lastMessage = messagesContainer.lastElementChild;
            if (lastMessage) lastMessage.setAttribute('data-timestamp', msg.timestamp);
          }
        });
      }
      this.hideFollowUpNotification();
    }
  }
}

// Initialize the chat widget
window.shopifyDirectN8NChatWidget = new DirectN8NChatWidget();